import { nonnegativeNumberQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";
import ScrapTrackingViewportPlot from "components/scrapTracking/ScrapTrackingViewportPlot";
import { Layout, PlotData } from "plotly.js";
import { HeatSeries } from "src/store/api/generatedApi";
import { ChunkData } from "components/scrapTracking/helpers";

interface Props {
  heatsData:
    | { series: HeatSeries[]; available_ignored_material_sets: string[][] }
    | undefined;
  start: Date;
  end: Date;
  chunks: ChunkData<unknown>[];
}

const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

const getStrikeCount = (
  heat: HeatSeries,
  ignoredMaterials: string[]
): number | null => {
  // Find the ScrapEye mix with matching ignored materials
  const scrapEyeMix = heat.mixes.find(
    (mix) =>
      mix.source === "ScrapEye" &&
      arraysEqual(mix.ignored_materials, ignoredMaterials)
  );
  if (!scrapEyeMix) return null;

  const liftStrikesData = scrapEyeMix.summary_data.find(
    ({ name }) => name === "liftStrikes"
  );
  if (liftStrikesData?.value == null) return null;

  return typeof liftStrikesData.value === "number"
    ? liftStrikesData.value
    : null;
};

const formatIgnoredMaterialsLabel = (ignoredMaterials: string[]): string => {
  if (ignoredMaterials.length === 0) return "Regular";
  return `No ${ignoredMaterials.join(" or ")}`;
};

export const StrikesOverTimePlot = ({
  heatsData,
  start,
  end,
  chunks,
}: Props) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();

  const loadingShapes = useMemo(() => {
    return chunks
      .filter((chunk) => chunk.value.status !== "success")
      .map((chunk) => ({
        type: "rect" as const,
        xref: "x" as const,
        yref: "paper" as const,
        x0: new Date(chunk.start),
        x1: new Date(chunk.end),
        y0: 0,
        y1: 1,
        fillcolor: "grey",
        opacity: 0.15,
        layer: "below" as const,
        line: { width: 0 },
      }));
  }, [chunks]);

  // Get all available ignored material sets
  const ignoredMaterialSets = useMemo(() => {
    return heatsData?.available_ignored_material_sets ?? [];
  }, [heatsData]);

  // Track which scenario is selected for the scatter plot
  const [selectedScenarioIndex, setSelectedScenarioIndex] = useNavigationQuery(
    "analysisScenario",
    0,
    nonnegativeNumberQuery,
    "replace"
  );

  const { scenarioStats, cumulativePlotData, strikeStatusDataByScenario } =
    useMemo(() => {
      if (!heatsData?.series || ignoredMaterialSets.length === 0) {
        return {
          scenarioStats: [],
          cumulativePlotData: [],
          strikeStatusDataByScenario: [],
        };
      }

      // Sort heats by start time
      const sortedHeats = [...heatsData.series].sort(
        (a, b) =>
          new Date(a.start_timestamp).getTime() -
          new Date(b.start_timestamp).getTime()
      );

      // Colors for different scenarios (rotate through palette)
      const scenarioColors = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.info.main,
        theme.palette.warning.main,
      ];

      const cumulativeTraces: Partial<PlotData>[] = [];
      const stats: {
        label: string;
        total: number;
        withStrikes: number;
        totalStrikes: number;
        color: string;
      }[] = [];

      // Calculate strike status data for each scenario
      const allScenarioStrikeData: Partial<PlotData>[][] = [];

      ignoredMaterialSets.forEach((ignoredMaterials, idx) => {
        const label = formatIgnoredMaterialsLabel(ignoredMaterials);
        const color =
          scenarioColors[idx % scenarioColors.length] ??
          theme.palette.grey[600];

        const cumulativeX: Date[] = [];
        const cumulativeY: number[] = [];
        let runningTotal = 0;
        let heatsWithStrikes = 0;
        let totalStrikes = 0;
        let totalHeats = 0;

        // Strike categories for this scenario
        const strikeCategories: Record<
          "zero" | "one" | "twoPlus",
          { x: Date[]; y: number[] }
        > = {
          zero: { x: [], y: [] },
          one: { x: [], y: [] },
          twoPlus: { x: [], y: [] },
        };

        sortedHeats.forEach((heat) => {
          const strikeCount = getStrikeCount(heat, ignoredMaterials);
          if (strikeCount === null) return;

          totalHeats++;
          const timestamp = new Date(heat.start_timestamp);

          if (strikeCount > 0) {
            heatsWithStrikes++;
            totalStrikes += strikeCount;
          }

          // Categorize for scatter plot
          if (strikeCount === 0) {
            strikeCategories.zero.x.push(timestamp);
            strikeCategories.zero.y.push(0);
          } else if (strikeCount === 1) {
            strikeCategories.one.x.push(timestamp);
            strikeCategories.one.y.push(1);
          } else {
            strikeCategories.twoPlus.x.push(timestamp);
            strikeCategories.twoPlus.y.push(2);
          }

          // Update cumulative
          runningTotal += strikeCount;
          cumulativeX.push(timestamp);
          cumulativeY.push(runningTotal);
        });

        stats.push({
          label,
          total: totalHeats,
          withStrikes: heatsWithStrikes,
          totalStrikes,
          color,
        });

        // Add cumulative trace for this scenario
        cumulativeTraces.push({
          x: cumulativeX,
          y: cumulativeY,
          type: "scatter",
          mode: "lines",
          name: label,
          line: {
            color,
            width: 2,
            shape: "hv",
          },
          hovertemplate: `${label}: %{y} total strikes<extra></extra>`,
        });

        // Create strike status scatter plot for this scenario
        const strikeStatusTraces: Partial<PlotData>[] = [
          {
            x: strikeCategories.zero.x,
            y: strikeCategories.zero.y,
            type: "scatter",
            mode: "markers",
            name: t("noStrikes"),
            marker: {
              color: theme.palette.success.main,
              size: 8,
              symbol: "circle",
            },
            hovertemplate: `${t("noStrikes")}<extra></extra>`,
          },
          {
            x: strikeCategories.one.x,
            y: strikeCategories.one.y,
            type: "scatter",
            mode: "markers",
            name: t("oneStrike"),
            marker: {
              color: theme.palette.warning.main,
              size: 10,
              symbol: "circle",
            },
            hovertemplate: `${t("oneStrike")}<extra></extra>`,
          },
          {
            x: strikeCategories.twoPlus.x,
            y: strikeCategories.twoPlus.y,
            type: "scatter",
            mode: "markers",
            name: t("twoOrMoreStrikes"),
            marker: {
              color: theme.palette.error.main,
              size: 12,
              symbol: "circle",
            },
            hovertemplate: `${t("twoOrMoreStrikes")}<extra></extra>`,
          },
        ];

        allScenarioStrikeData.push(strikeStatusTraces);
      });

      return {
        scenarioStats: stats,
        cumulativePlotData: cumulativeTraces,
        strikeStatusDataByScenario: allScenarioStrikeData,
      };
    }, [heatsData, ignoredMaterialSets, theme, t]);

  const cumulativeLayout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        range: [start, end],
        type: "date",
        showgrid: true,
        gridcolor: theme.palette.grey[100],
      },
      yaxis: {
        showgrid: true,
        gridcolor: theme.palette.grey[100],
        title: { text: t("cumulativeStrikes") },
        rangemode: "tozero",
      },
      margin: { t: 20, r: 20, l: 60, b: 40 },
      height: 300,
      showlegend: true,
      legend: { orientation: "h", y: 1.15 },
      hovermode: "x unified",
      dragmode: "pan",
      shapes: loadingShapes,
    }),
    [start, end, t, loadingShapes, theme.palette.grey]
  );

  const strikeStatusLayout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        range: [start, end],
        type: "date",
        showgrid: true,
        gridcolor: theme.palette.grey[100],
      },
      yaxis: {
        showgrid: false,
        tickvals: [0, 1, 2],
        ticktext: [t("noStrikes"), t("oneStrike"), t("twoOrMoreStrikes")],
        range: [-0.5, 2.5],
        fixedrange: true,
      },
      margin: { t: 20, r: 20, l: 120, b: 40 },
      height: 200,
      showlegend: true,
      legend: { orientation: "h", y: 1.2 },
      hovermode: "closest",
      dragmode: "pan",
      shapes: loadingShapes,
    }),
    [start, end, t, loadingShapes, theme.palette.grey]
  );

  const selectedStrikeStatusData =
    strikeStatusDataByScenario[selectedScenarioIndex] ?? [];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t("strikesOverTime")}
        </Typography>

        {/* Stats summary for each scenario */}
        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
          {scenarioStats.map((scenario, idx) => {
            const strikePct =
              scenario.total > 0
                ? ((scenario.withStrikes / scenario.total) * 100).toFixed(1)
                : "0";
            const isSelected = idx === selectedScenarioIndex;
            return (
              <Box
                key={scenario.label}
                onClick={() => {
                  setSelectedScenarioIndex(idx);
                }}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: `2px solid ${scenario.color}`,
                  minWidth: 140,
                  cursor: "pointer",
                  bgcolor: isSelected ? `${scenario.color}15` : "transparent",
                  transition: "background-color 0.2s",
                  "&:hover": {
                    bgcolor: `${scenario.color}10`,
                  },
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {scenario.label}
                </Typography>
                <Typography variant="h5" sx={{ color: scenario.color }}>
                  {scenario.totalStrikes}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {scenario.withStrikes}/{scenario.total} {t("heats")} (
                  {strikePct}%)
                </Typography>
              </Box>
            );
          })}
        </Stack>

        {/* Strike status per heat for selected scenario */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {t("strikeStatusPerHeat")} -{" "}
          {scenarioStats[selectedScenarioIndex]?.label ?? ""}
        </Typography>
        <Box sx={{ height: 200 }}>
          <ScrapTrackingViewportPlot
            data={selectedStrikeStatusData}
            layout={strikeStatusLayout}
            style={{ width: "100%", height: "100%" }}
            config={{
              displayModeBar: false,
              responsive: true,
              scrollZoom: false,
            }}
          />
        </Box>

        {/* Cumulative strikes over time for all scenarios */}
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mt: 2, mb: 1 }}
        >
          {t("cumulativeStrikesAllScenarios")}
        </Typography>
        <Box sx={{ height: 300 }}>
          <ScrapTrackingViewportPlot
            data={cumulativePlotData}
            layout={cumulativeLayout}
            style={{ width: "100%", height: "100%" }}
            config={{
              displayModeBar: false,
              responsive: true,
              scrollZoom: false,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
